import React, { useMemo } from "react"
import styles from "./index.module.scss"
import { useForm } from "react-hook-form"
import { WISH_ITEM_DEFINITIONS } from "src/models/RewardRequest/WISH_ITEM_DEFINITIONS"
import { REQUEST_REASON_DEFINITIONS } from "src/models/RewardRequest/REQUEST_REASON_DEFINITIONS"
import { GIFT_TYPE_DEFINITIONS } from "src/models/gift/GIFT_TYPE_DEFINITIONS"
import clsx from "clsx"

const MIN_GIFT_TOTAL_AMOUNT = 1000

export type FormParams = {
  wishItemId: number
  wishItemMessage: string
  giftItemId: string
  amount: number
  requestReasonId: number
}

type Props = {
  onSubmit: (data: FormParams) => void
  isHiddenSubmitButton: boolean
}

const RewardRequestForm: React.FC<Props> = ({
  onSubmit,
  isHiddenSubmitButton,
}) => {
  const { handleSubmit, register, setError, clearErrors, errors, watch } =
    useForm<FormParams>({
      defaultValues: {
        wishItemId: undefined,
        wishItemMessage: "",
        giftItemId: undefined,
        amount: 0,
        requestReasonId: undefined,
      },
    })

  const watchGiftItemId = watch("giftItemId")

  const watchAmount = watch("amount")

  const totalAmount = useMemo(() => {
    const giftType = GIFT_TYPE_DEFINITIONS.find(
      (v) => v.id === parseInt(watchGiftItemId, 10),
    )

    return giftType ? giftType.amount * watchAmount : 0
  }, [watchAmount, watchGiftItemId])

  const isValidTotalAmount = useMemo(() => {
    return totalAmount >= MIN_GIFT_TOTAL_AMOUNT
  }, [totalAmount])

  const invalidTotalAmountMessage = `${MIN_GIFT_TOTAL_AMOUNT.toLocaleString()}
  TG以上のギフト額を設定してください。`

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <ul className={styles.formItems}>
        <li className={styles.formItem}>
          <h3 className={styles.formItem_title}>
            ほしい品物
            <span className={styles.formItem_annotation}>※必須</span>
          </h3>

          <div className={styles.formItem_body}>
            <div className={styles.selectWrapper}>
              <select
                defaultValue={""}
                className={styles.select}
                name="wishItemId"
                ref={register({ required: true })}
              >
                <option value="" disabled>
                  品物を選択
                </option>
                {WISH_ITEM_DEFINITIONS.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.value}
                  </option>
                ))}
              </select>
            </div>
            {errors.wishItemId && (
              <p className={styles.errorMessage}>
                ほしい品物を選択してください。
              </p>
            )}
          </div>
        </li>

        <li className={styles.formItem}>
          <h3 className={styles.formItem_title}>
            ほしい品物の詳細
            <span className={styles.formItem_annotation}>※必須</span>
          </h3>
          <p className={styles.formItem_text}>
            どのような品物が欲しいのか具体的に記入してください。(小説の場合は文字数、イラストの場合は点数など)
          </p>

          <div className={styles.formItem_body}>
            <textarea
              name="wishItemMessage"
              ref={register({ required: true })}
              placeholder={`例：主人公の過去を短いショートストーリー1話(10000文字程度)として書いてほしいです。\n例：ヒロインのイラストを1点フルカラーで描き下ろしてほしいです。`}
              className={styles.textarea}
            ></textarea>
            {errors.wishItemMessage && (
              <p className={styles.errorMessage}>
                ほしい品物の詳細を入力してください。
              </p>
            )}
          </div>
        </li>

        <li className={styles.formItem}>
          <h3 className={styles.formItem_title}>
            ギフト額設定
            <span className={styles.formItem_annotation}>※必須</span>
          </h3>
          <p className={styles.formItem_text}>
            依頼に応えてもらうためにクリエイターへ贈るギフト額を設定してください。
            <br />
            支払いができない場合は品物の制作が開始ができませんので、ご注意ください。
            <span className={styles.formItem_text_warning}>
              ギフト額は{MIN_GIFT_TOTAL_AMOUNT.toLocaleString()}
              TG以上を設定してください。
            </span>
          </p>

          <div className={clsx(styles.formItem_body, styles.settingGift)}>
            <ul className={styles.giftList}>
              {GIFT_TYPE_DEFINITIONS.map((type) => (
                <li className={styles.giftItem} key={type.id}>
                  <input
                    className={styles.giftItem_input}
                    type="radio"
                    name="giftItemId"
                    id={`giftItemInput${type.id}`}
                    value={type.id}
                    ref={register({ required: true })}
                  />
                  <label
                    className={styles.giftItem_label}
                    htmlFor={`giftItemInput${type.id}`}
                  >
                    <span className={styles.giftItem_type}>
                      <img
                        className={styles.giftItem_type_icon}
                        src={type.iconUrl}
                      />
                      <span className={styles.giftItem_type_name}>
                        {type.name}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>

            <div className={styles.giftAmountWrapper}>
              <div className={styles.giftAmount}>
                <label className={styles.giftAmount_label}>
                  選択したギフトの数
                </label>
                <input
                  name="amount"
                  type="number"
                  min={1}
                  disabled={!watchGiftItemId}
                  onChange={() => {
                    if (!isValidTotalAmount) {
                      setError("amount", {
                        message: invalidTotalAmountMessage,
                      })
                    } else {
                      clearErrors("amount")
                    }
                  }}
                  ref={register({
                    required: true,
                    validate: {
                      totalAmount: () =>
                        isValidTotalAmount || invalidTotalAmountMessage,
                    },
                  })}
                  className={styles.giftAmount_input}
                ></input>
              </div>

              <div className={styles.giftTotalAmount}>
                <label className={styles.giftTotalAmount_label}>
                  設定ギフト額
                </label>
                <p className={styles.giftTotalAmount_value}>
                  <span>{totalAmount}</span>TG
                </p>
              </div>
            </div>

            {(errors.giftItemId || errors.amount?.type === "required") && (
              <p className={styles.errorMessage}>
                ギフトを選択して、個数を入力してください。
              </p>
            )}

            {!isValidTotalAmount && errors.amount && (
              <p className={styles.errorMessage}>{errors.amount.message}</p>
            )}
          </div>
        </li>

        <li className={styles.formItem}>
          <h3 className={styles.formItem_title}>
            依頼した理由
            <span className={styles.formItem_annotation}>※必須</span>
          </h3>

          <div className={styles.formItem_body}>
            <div className={styles.selectWrapper}>
              <select
                defaultValue={""}
                className={styles.select}
                name="requestReasonId"
                ref={register({ required: true })}
              >
                <option value="" disabled>
                  依頼した理由を選択
                </option>
                {REQUEST_REASON_DEFINITIONS.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.value}
                  </option>
                ))}
              </select>
            </div>
            {errors.requestReasonId && (
              <p className={styles.errorMessage}>
                依頼した理由を入力してください。
              </p>
            )}
          </div>
        </li>
      </ul>

      <div className={styles.submit}>
        <p className={styles.submit_text}>
          依頼は取り消すことができません。確認の上、送信してください。
          <br />
          依頼の承諾状況はたいあっぷギフト管理から確認できます。
        </p>
        {!isHiddenSubmitButton && (
          <button className={styles.submit_button}>送信</button>
        )}
      </div>
    </form>
  )
}

export default RewardRequestForm
